import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft,
  Plus, 
  DollarSign,
  Loader2,
  MoreVertical,
  Server,
  Mail,
  Globe,
  Wrench,
  Package,
  Pencil,
  Trash2,
  Calendar,
  Building2,
  FolderKanban
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryOptions = [
  { value: 'hosting', label: 'Hosting', icon: Server, color: 'bg-blue-50 text-blue-600' },
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-purple-50 text-purple-600' },
  { value: 'domain', label: 'Domain', icon: Globe, color: 'bg-emerald-50 text-emerald-600' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'bg-amber-50 text-amber-600' },
  { value: 'other', label: 'Other', icon: Package, color: 'bg-slate-100 text-slate-600' }
];

const cycleOptions = [
  { value: 'monthly', label: 'Monthly', short: '/mo' },
  { value: 'quarterly', label: 'Quarterly', short: '/qtr' },
  { value: 'yearly', label: 'Yearly', short: '/yr' }
];

const BillingDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'hosting',
    amount: '',
    billing_cycle: 'monthly',
    project_id: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const fetchData = async () => {
    try {
      const [summaryRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/billing-summary/${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/projects?organization_id=${tenantId}`, getAuthHeaders())
      ]);
      setSummary(summaryRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/billing-items`, {
        ...formData,
        tenant_id: tenantId,
        amount: parseFloat(formData.amount),
        project_id: formData.project_id === 'none' ? null : (formData.project_id || null)
      }, getAuthHeaders());
      toast.success('Billing item added');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create billing item');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/billing-items/${selectedItem.id}`, {
        ...formData,
        amount: parseFloat(formData.amount),
        project_id: formData.project_id === 'none' ? null : (formData.project_id || null)
      }, getAuthHeaders());
      toast.success('Billing item updated');
      setIsEditOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update billing item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this billing item?')) return;
    
    try {
      await axios.delete(`${API_URL}/billing-items/${itemId}`, getAuthHeaders());
      toast.success('Billing item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete billing item');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API_URL}/billing-items/${item.id}`, {
        is_active: !item.is_active
      }, getAuthHeaders());
      toast.success(item.is_active ? 'Item deactivated' : 'Item activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      amount: item.amount.toString(),
      billing_cycle: item.billing_cycle,
      project_id: item.project_id || '',
      is_active: item.is_active
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'hosting',
      amount: '',
      billing_cycle: 'monthly',
      project_id: '',
      is_active: true
    });
    setSelectedItem(null);
  };

  const getCategoryStyle = (category) => {
    return categoryOptions.find(c => c.value === category) || categoryOptions[4];
  };

  const getCycleLabel = (cycle) => {
    return cycleOptions.find(c => c.value === cycle)?.short || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-700">Tenant not found</h2>
        <Button variant="link" onClick={() => navigate('/billing')}>Back to Billing</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/billing')}
          data-testid="back-to-billing-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Building2 size={24} className="text-slate-400" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {summary.tenant_name}
            </h1>
          </div>
          <p className="text-slate-500">Recurring Billing Dashboard</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-item-btn">
              <Plus size={18} className="mr-2" />
              Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add Billing Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Website Hosting"
                  required
                  data-testid="item-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select
                    value={formData.billing_cycle}
                    onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cycleOptions.map(cycle => (
                        <SelectItem key={cycle.value} value={cycle.value}>{cycle.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Project (Optional)</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Link to project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                  Add Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Calendar size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Monthly</p>
                <p className="text-2xl font-bold text-slate-900">${summary.total_monthly.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <Calendar size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Quarterly</p>
                <p className="text-2xl font-bold text-slate-900">${summary.total_quarterly.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Calendar size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Yearly</p>
                <p className="text-2xl font-bold text-slate-900">${summary.total_yearly.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      {Object.keys(summary.items_by_category).length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">By Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {categoryOptions.map(cat => {
                const data = summary.items_by_category[cat.value];
                if (!data) return null;
                const Icon = cat.icon;
                return (
                  <div key={cat.value} className="text-center p-4 bg-slate-50 rounded-lg">
                    <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon size={20} />
                    </div>
                    <p className="text-lg font-bold text-slate-900">${data.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{cat.label} ({data.count})</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Items List */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">All Billing Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {summary.items.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No billing items yet</h3>
              <p className="text-slate-500 mt-1">Add hosting, email, or other recurring charges</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {summary.items.map((item) => {
                const catStyle = getCategoryStyle(item.category);
                const Icon = catStyle.icon;
                return (
                  <div 
                    key={item.id} 
                    className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${!item.is_active ? 'opacity-50' : ''}`}
                    data-testid={`billing-item-${item.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${catStyle.color} flex items-center justify-center`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{item.name}</p>
                        {item.project_name && (
                          <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                            <FolderKanban size={10} />
                            {item.project_name}
                          </span>
                        )}
                        {!item.is_active && (
                          <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded">Inactive</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-sm text-slate-500 truncate">{item.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        ${item.amount.toLocaleString()}
                        <span className="text-xs text-slate-400 ml-1">{getCycleLabel(item.billing_cycle)}</span>
                      </p>
                      <p className="text-xs text-slate-400 capitalize">{item.category}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(item)}>
                          <Pencil size={14} className="mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(item)}>
                          {item.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Billing Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Billing Cycle</Label>
                <Select
                  value={formData.billing_cycle}
                  onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cycleOptions.map(cycle => (
                      <SelectItem key={cycle.value} value={cycle.value}>{cycle.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Link to project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Update Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingDetail;
